import { useState, useEffect, useMemo, useRef } from 'react';
import {
  SeatingAreaDto,
  SeatingReservationDto,
  ShiftAvailabilityDto,
  ShiftSlotDto,
} from '@/services/platform/api/model';
import type { WhenTab, WhenSheetProps } from '../types';
import { cn } from '@/lib/core/utils/cn';
import { useClientTranslation } from '@/lib/core/i18n';
import { formatTime } from '@/lib/core/utils';
import { Icon } from '@/components/ui/icon';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { useLocaleStore } from '@/lib/core/stores';
import { useLocation } from 'react-router-dom';
import {
  getSevenRoomsAvailability,
  getSevenRoomsSeatingAreas,
  requestSevenRoomsReservation,
} from '@/services/platform/data/booking/sevenrooms';
import { BookingRequestSheet } from '@/components/shared/booking/actions/request-sheet';
import { BookShiftTimeSlots } from '@/components/shared/booking/common/book/shift-time-slots';

export function WhenSheet({
  isOpen,
  onClose,
  cmsProps,
  booking,
  onBookingChange,
  onRequestSuccess,
}: WhenSheetProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const location = useLocation();
  const categoryId = (location.state as { categoryId?: string } | null)?.categoryId;

  const [tab, setTab] = useState<WhenTab>('date');
  const [date, setDate] = useState<Date | undefined>(booking.date ?? undefined);
  const [room, setRoom] = useState<string | null>(booking.room);
  const [time, setTime] = useState<string | null>(booking.time);

  useEffect(() => {
    if (!isOpen) return;
    setTab('date');
    setDate(booking.date ?? undefined);
    setRoom(booking.room);
    setTime(booking.time);
  }, [isOpen]);
  const [seatingAreas, setSeatingAreas] = useState<SeatingAreaDto[]>([]);
  const [shifts, setShifts] = useState<ShiftAvailabilityDto[]>([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [requestSheetOpen, setRequestSheetOpen] = useState(false);
  const [pendingSlotTime, setPendingSlotTime] = useState<string | null>(null);
  const [isRequestAnyway, setIsRequestAnyway] = useState(false);
  const requestedReservationRef = useRef<SeatingReservationDto | null>(null);

  useEffect(() => {
    if (!isOpen || !booking.venue?.venueNodeKey) return;
    getSevenRoomsSeatingAreas(
      booking.venue.venueNodeKey,
      { serviceCategoryNodeKey: categoryId },
      locale
    ).then(result => setSeatingAreas(result ?? []));
  }, [isOpen, booking.venue?.venueNodeKey, categoryId, locale]);

  useEffect(() => {
    if (!isOpen || !booking.venue?.venueNodeKey || !date) return;
    const seatingAreaPublicGuid = seatingAreas.find(a => a.name === room)?.publicGuid ?? undefined;

    setLoadingAvailability(true);
    getSevenRoomsAvailability(
      booking.venue.venueNodeKey,
      {
        date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`,
        guestCount: booking.guestCount != null ? String(booking.guestCount) : undefined,
        seatingAreaPublicGuid,
        serviceCategoryNodeKey: categoryId,
      },
      locale
    ).then(result => {
      setShifts(result ?? []);
      setLoadingAvailability(false);
    });
  }, [isOpen, booking.venue?.venueNodeKey, date, room, booking.guestCount, categoryId, locale]);

  const unavailableTimes = useMemo(() => {
    const set = new Set<string>();
    for (const shift of shifts) {
      for (const slot of shift.slots ?? []) {
        if (!slot.isAvailable && slot.time) set.add(slot.time);
      }
    }
    return set;
  }, [shifts]);

  const handleRoomChange = (roomName: string | null) => {
    setRoom(roomName);
    setTime(null);
    setIsRequestAnyway(false);
    onBookingChange({ room: roomName, time: null });
  };

  const handleDaySelect = (day: Date | undefined) => {
    setDate(day);
    setRoom(null);
    setTime(null);
    setShifts([]);
    setIsRequestAnyway(false);
    onBookingChange({ date: day ?? null, room: null, time: null });
    if (day) setTab('roomtime');
  };

  const handleTimeClick = (slotTime: string, slotData: ShiftSlotDto) => {
    const currentAreaGuid = seatingAreas.find(a => a.name === room)?.publicGuid;

    let matchedArea =
      room && currentAreaGuid
        ? slotData.availableSeatingAreas?.find(a => a.publicGuid === currentAreaGuid)
        : undefined;

    if (!room || !currentAreaGuid) {
      const firstBookArea = slotData.availableSeatingAreas?.find(a => a.type === 'book');
      if (firstBookArea) {
        setRoom(firstBookArea.name ?? null);
        onBookingChange({ room: firstBookArea.name ?? null });
        matchedArea = firstBookArea;
      } else {
        setPendingSlotTime(slotTime);
        setRequestSheetOpen(true);
        return;
      }
    }

    if (matchedArea?.type === 'request' || unavailableTimes.has(slotTime)) {
      setPendingSlotTime(slotTime);
      setRequestSheetOpen(true);
      return;
    }

    setIsRequestAnyway(false);
    setTime(slotTime);
    onBookingChange({ time: slotTime, isRequestAnyway: false });
  };

  const handleSelect = () => {
    if (date && time) {
      const seatingAreaPublicGuid = seatingAreas.find(a => a.name === room)?.publicGuid ?? undefined;
      onBookingChange({ date, room, time, isRequestAnyway, seatingAreaPublicGuid });
      onClose();
    }
  };

  const canSelect = !!date && !!time;

  // Dynamic tab labels
  const dateTabLabel = date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Date';
  const roomTimeTabLabel =
    room && time ? `${room}, ${formatTime(time)}` : room ? room : t('App.Labels.ParkTime');

  const title = cmsProps?.title ?? t('App.Labels.When');
  const subtitle = cmsProps?.subtitle ?? t('App.Labels.SelectDate');

  const assistance =
    cmsProps?.assistanceTitle && cmsProps?.assistanceDescription
      ? {
          title: cmsProps.assistanceTitle,
          description: cmsProps.assistanceDescription,
          descriptionForMember: cmsProps.assistanceDescriptionForMember,
        }
      : null;

  return (
    <>
      <Drawer
        open={isOpen && !requestSheetOpen}
        onOpenChange={open => {
          if (!open) onClose();
        }}
      >
        <DrawerContent className="border-jncquoi-neutral-200 h-[80dvh] min-h-[min(650px,100dvh)] border-t-[0.5px]">
          <DrawerHeader className="shrink-0 gap-2 pt-8 pb-3">
            <div className="flex items-center gap-8">
              <DrawerTitle className="flex-1 text-[32px] font-medium tracking-[0.5px]">{title}</DrawerTitle>
              <DrawerClose
                className="flex size-11 cursor-pointer items-center justify-center rounded-full"
                aria-label="Close"
              >
                <Icon
                  name="xmark"
                  width={24}
                  height={24}
                  strokeWidth={1.5}
                  className="text-jncquoi-neutral-1000"
                />
              </DrawerClose>
            </div>
            <DrawerDescription className="text-[16px] text-[#707070]">{subtitle}</DrawerDescription>
          </DrawerHeader>
          <div data-base-ui-swipe-ignore className="flex-1 overflow-y-auto">
            {/* pb-24 reserves space so sticky button never overlaps last content item */}
            <div className="flex flex-col pb-6">
              {/* Tabs */}
              <div className="mx-auto mt-3 mb-5 flex w-fit items-center rounded-[20px] bg-[#f4f4f4] p-1">
                {(['date', 'roomtime'] as WhenTab[]).map(tabKey => (
                  <button
                    key={tabKey}
                    type="button"
                    onClick={() => setTab(tabKey)}
                    disabled={tabKey === 'roomtime' && !date}
                    className={cn(
                      'font-pp-neue-montreal rounded-[20px] px-4 py-1.5 text-[14px] tracking-[0.5px] transition-all duration-200 disabled:opacity-40',
                      tab === tabKey
                        ? 'bg-white font-normal text-[#1a1a1a]'
                        : 'bg-[#f4f4f4] font-semibold text-[#1a1a1a]'
                    )}
                  >
                    {tabKey === 'date' ? dateTabLabel : roomTimeTabLabel}
                  </button>
                ))}
              </div>

              {/* Date tab */}
              {tab === 'date' && (
                <Calendar
                  isOpen={isOpen}
                  selected={date}
                  onSelect={handleDaySelect}
                  locale={locale}
                  assistance={assistance ?? undefined}
                />
              )}

              {/* Room, time tab */}
              {tab === 'roomtime' && (
                <BookShiftTimeSlots
                  seatingAreas={seatingAreas}
                  shifts={shifts}
                  loadingAvailability={loadingAvailability}
                  selectedRoom={room}
                  selectedTime={time}
                  showShifts={cmsProps?.showShifts}
                  areaLabel={t('App.Labels.Park')}
                  slotNotAvailableTitle={cmsProps?.slotNotAvailableTitle}
                  slotNotAvailableDescription={cmsProps?.slotNotAvailableDescription}
                  assistance={assistance}
                  onRoomChange={handleRoomChange}
                  onTimeClick={handleTimeClick}
                />
              )}
            </div>

            {/* SELECT — sticky at bottom of scroll container */}
            <div className="sticky bottom-0 w-full shrink-0 border-t-[0.5px] border-[#c8c8c8] bg-white px-4 pt-6 pb-10.5">
              <Button
                variant="primary-solid"
                palette="dark"
                size="lg"
                className="w-full"
                disabled={!canSelect}
                onClick={handleSelect}
              >
                {cmsProps?.selectButtonLabel ?? t('App.Labels.Select')}
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <BookingRequestSheet
        open={requestSheetOpen}
        onOpenChange={setRequestSheetOpen}
        headerTitle={cmsProps?.slotFullyBookedSheetTitle}
        title={cmsProps?.slotFullyBookedTitle}
        description={cmsProps?.slotFullyBookedDescription}
        seeAvailableSlotsLabel={cmsProps?.seeAvailableSlotsLabel}
        requestAnywayLabel={cmsProps?.requestAnywayLabel}
        onSeeAvailableSlots={() => setRequestSheetOpen(false)}
        onRequestAnyway={async () => {
          if (!pendingSlotTime) return;
          const seatingAreaPublicGuid = seatingAreas.find(a => a.name === room)?.publicGuid;
          const dateStr = date
            ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
            : undefined;
          const reservation = await requestSevenRoomsReservation(
            {
              venueNodeKey: booking?.venue?.venueNodeKey,
              seatingAreaPublicGuid,
              date: dateStr,
              time: pendingSlotTime,
              guestCount: booking.guestCount ?? undefined,
            },
            booking.idempotencyKey
          );
          if (!reservation) throw new Error('Request failed');
          requestedReservationRef.current = reservation;
          setTime(pendingSlotTime);
          setIsRequestAnyway(true);
          onBookingChange({ time: pendingSlotTime, isRequestAnyway: true, seatingAreaPublicGuid });
        }}
        onSuccess={() => {
          setRequestSheetOpen(false);
          onClose();
          if (requestedReservationRef.current) onRequestSuccess?.(requestedReservationRef.current);
        }}
      />
    </>
  );
}
